import { useState } from "react";
import _ from "lodash";
import {
  lookupListMarkup,
  uuidMapping,
} from "../../talents/talentListNew/helper";

const EMPTY_RANGE = { start_value: "", end_value: "" };

export const useRoleComposition = ({
  shiftRoleCompositionTemp,
  triggerUpdateShiftComp,
  showEditIcon,
  updateRoleOnEdit,
}) => {
  const [isEditable, setIsEditable] = useState(false);
  const [roleCompTags, setRoleCompTags] = useState([]);
  const [ageValues, setAgeValues] = useState(EMPTY_RANGE);
  const [weightValues, setWeightValues] = useState(EMPTY_RANGE);
  const [maxFeet, setMaxFeet] = useState("");
  const [maxInches, setMaxInches] = useState("");
  const [minFeet, setMinFeet] = useState("");
  const [minInches, setMinInches] = useState("");
  const [selectedGender, setSelectedGender] = useState([]);
  const [selectedEthnicity, setSelectedEthnicity] = useState([]);
  const [selectedEyeColor, setSelectedEyeColor] = useState([]);
  const [selectedHairColor, setSelectedHairColor] = useState([]);
  const [showCompositionPopover, setShowCompositionPopover] = useState(null);
  const [showCompositionParent, setShowCompositionParent] = useState(null);

  const getRoleCompTemp = () => _.cloneDeep(shiftRoleCompositionTemp);

  const updateRoleCompTemplate = () => {
    const roleCompTemp = getRoleCompTemp();
    const newTags = [];
    setRoleCompTags([]);

    if (!roleCompTemp?.value?.length) return;

    roleCompTemp.value.forEach((value) => {
      const getTag = _.filter(lookupListMarkup, { uuid: value?.person_attribute_uuid });
      let tagValues = value?.list
        ? value.list
        : { start_value: value?.start_value, end_value: value?.end_value };

      if (value.person_attribute_uuid === uuidMapping.height) {
        tagValues.start_value = `${parseInt(value?.start_value / 12)}'${parseInt(value?.start_value % 12)}"`;
        tagValues.end_value   = `${parseInt(value?.end_value / 12)}'${parseInt(value?.end_value % 12)}"`;
        setMinFeet(parseInt(value?.start_value / 12));
        setMinInches(parseInt(value?.start_value % 12));
        setMaxFeet(parseInt(value?.end_value / 12));
        setMaxInches(parseInt(value?.end_value % 12));
      }
      if (value.person_attribute_uuid === uuidMapping.weight)
        setWeightValues({ start_value: value?.start_value, end_value: value?.end_value });
      if (value.person_attribute_uuid === uuidMapping.age)
        setAgeValues({ start_value: value?.start_value, end_value: value?.end_value });
      if (value.person_attribute_uuid === uuidMapping.gender)    setSelectedGender(value.list);
      if (value.person_attribute_uuid === uuidMapping.ethnicity) setSelectedEthnicity(value.list);
      if (value.person_attribute_uuid === uuidMapping.eyeColor)  setSelectedEyeColor(value.list);
      if (value.person_attribute_uuid === uuidMapping.hairColor) setSelectedHairColor(value.list);

      newTags.push({ uuid: getTag[0].uuid, data: getTag[0].tag(tagValues) });
    });

    setRoleCompTags(newTags);
  };

  const addTagForEdit = (person_attribute_uuid, values) => {
    const existingTags = [...roleCompTags];
    const getTag = _.filter(lookupListMarkup, { uuid: person_attribute_uuid });
    const existingIndex = _.findIndex(existingTags, { uuid: person_attribute_uuid });
    const tagData = { uuid: getTag[0].uuid, data: getTag[0].tag(values) };

    if (existingIndex < 0) existingTags.push(tagData);
    else existingTags[existingIndex] = tagData;

    setRoleCompTags(existingTags);
    setShowCompositionPopover(null);
  };

  const buildPersonAttributeArr = (roleCompTemp) => {
    return roleCompTemp?.value?.length ? [...roleCompTemp.value] : [];
  };

  const upsertAttribute = (arr, uuid, newAttr) => {
    const index = _.findIndex(arr, { person_attribute_uuid: uuid });
    if (index < 0) arr.push(newAttr);
    else Object.assign(arr[index], newAttr);
  };

  const removeAttribute = (arr, uuid) => {
    const index = _.findIndex(arr, { person_attribute_uuid: uuid });
    if (index >= 0) arr.splice(index, 1);
  };

  const onRoleCompositionChange = (person_attribute_uuid, values) => {
    const roleCompTemp = getRoleCompTemp();
    const arr = buildPersonAttributeArr(roleCompTemp);

    if (person_attribute_uuid === uuidMapping.gender) {
      if (selectedGender.length)
        upsertAttribute(arr, uuidMapping.gender, {
          person_attribute_uuid: uuidMapping.gender,
          contains: false, start_value: null, end_value: null,
          has_all_in_list: false, list: selectedGender,
        });
      else removeAttribute(arr, uuidMapping.gender);
    }

    if (person_attribute_uuid === uuidMapping.age) {
      const { start_value, end_value } = values || ageValues;
      if (start_value && end_value && parseInt(end_value) > parseInt(start_value))
        upsertAttribute(arr, uuidMapping.age, {
          person_attribute_uuid: uuidMapping.age,
          contains: false, has_all_in_list: false, list: null,
          start_value: parseInt(start_value),
          end_value: parseInt(end_value),
        });
      else removeAttribute(arr, uuidMapping.age);
    }

    if (person_attribute_uuid === uuidMapping.ethnicity) {
      if (selectedEthnicity.length)
        upsertAttribute(arr, uuidMapping.ethnicity, {
          person_attribute_uuid: uuidMapping.ethnicity,
          contains: false, start_value: null, end_value: null,
          has_all_in_list: false, list: selectedEthnicity,
        });
      else removeAttribute(arr, uuidMapping.ethnicity);
    }

    if (person_attribute_uuid === uuidMapping.height) {
      const minTotal = parseInt(minFeet) * 12 + parseInt(minInches);
      const maxTotal = parseInt(maxFeet) * 12 + parseInt(maxInches);
      if (maxFeet > 0 && minFeet > 0 && maxTotal > minTotal)
        upsertAttribute(arr, uuidMapping.height, {
          person_attribute_uuid: uuidMapping.height,
          contains: false, has_all_in_list: false, list: null,
          start_value: minTotal, end_value: maxTotal,
        });
      else removeAttribute(arr, uuidMapping.height);
    }

    if (person_attribute_uuid === uuidMapping.weight) {
      const { start_value, end_value } = values || weightValues;
      if (start_value && end_value && parseInt(end_value) > parseInt(start_value))
        upsertAttribute(arr, uuidMapping.weight, {
          person_attribute_uuid: uuidMapping.weight,
          contains: false, has_all_in_list: false, list: null,
          start_value: parseInt(start_value),
          end_value: parseInt(end_value),
        });
      else removeAttribute(arr, uuidMapping.weight);
    }

    if (person_attribute_uuid === uuidMapping.eyeColor) {
      if (selectedEyeColor.length)
        upsertAttribute(arr, uuidMapping.eyeColor, {
          person_attribute_uuid: uuidMapping.eyeColor,
          contains: false, start_value: null, end_value: null,
          has_all_in_list: false, list: selectedEyeColor,
        });
      else removeAttribute(arr, uuidMapping.eyeColor);
    }

    if (person_attribute_uuid === uuidMapping.hairColor) {
      if (selectedHairColor.length)
        upsertAttribute(arr, uuidMapping.hairColor, {
          person_attribute_uuid: uuidMapping.hairColor,
          contains: false, start_value: null, end_value: null,
          has_all_in_list: false, list: selectedHairColor,
        });
      else removeAttribute(arr, uuidMapping.hairColor);
    }

    setShowCompositionPopover(null);
    roleCompTemp["value"] = arr;
    triggerUpdateShiftComp({ roleCompDetails: roleCompTemp });
  };

  const onCloseRoleCompTag = (tag) => {
    const { uuid } = tag;
    const resetMap = {
      [uuidMapping.age]:       () => setAgeValues(EMPTY_RANGE),
      [uuidMapping.weight]:    () => setWeightValues(EMPTY_RANGE),
      [uuidMapping.height]:    () => { setMaxFeet(""); setMaxInches(""); setMinFeet(""); setMinInches(""); },
      [uuidMapping.gender]:    () => setSelectedGender([]),
      [uuidMapping.ethnicity]: () => setSelectedEthnicity([]),
      [uuidMapping.eyeColor]:  () => setSelectedEyeColor([]),
      [uuidMapping.hairColor]: () => setSelectedHairColor([]),
    };
    resetMap[uuid]?.();

    if (!showEditIcon) {
      const roleCompTemp = getRoleCompTemp();
      const arr = roleCompTemp.value;
      const index = _.findIndex(arr, { person_attribute_uuid: uuid });
      arr.splice(index, 1);
      roleCompTemp["value"] = arr;
      triggerUpdateShiftComp({ roleCompDetails: roleCompTemp });
    } else {
      setRoleCompTags(prev => prev.filter(t => t.uuid !== uuid));
    }
  };

  const clearMemoryLeak = (person_attribute_uuid) => {
    const roleCompTemp = getRoleCompTemp();
    const isValueSelected = roleCompTemp?.value?.some(
      t => t.person_attribute_uuid === person_attribute_uuid
    );
    if (isValueSelected) return;

    const resetMap = {
      [uuidMapping.age]:       () => setAgeValues(EMPTY_RANGE),
      [uuidMapping.weight]:    () => setWeightValues(EMPTY_RANGE),
      [uuidMapping.height]:    () => { setMaxFeet(""); setMaxInches(""); setMinFeet(""); setMinInches(""); },
      [uuidMapping.gender]:    () => setSelectedGender([]),
      [uuidMapping.ethnicity]: () => setSelectedEthnicity([]),
      [uuidMapping.eyeColor]:  () => setSelectedEyeColor([]),
      [uuidMapping.hairColor]: () => setSelectedHairColor([]),
    };
    resetMap[person_attribute_uuid]?.();
  };

  const onSaveEdit = () => {
    const arr = [];

    if (selectedGender.length)
      arr.push({ person_attribute_uuid: uuidMapping.gender, contains: false, start_value: null, end_value: null, has_all_in_list: false, list: selectedGender });

    if (ageValues.start_value && ageValues.end_value)
      arr.push({ person_attribute_uuid: uuidMapping.age, contains: false, has_all_in_list: false, list: null, start_value: parseInt(ageValues.start_value), end_value: parseInt(ageValues.end_value) });

    if (selectedEthnicity.length)
      arr.push({ person_attribute_uuid: uuidMapping.ethnicity, contains: false, start_value: null, end_value: null, has_all_in_list: false, list: selectedEthnicity });

    if (maxFeet > 0 && minFeet > 0 && parseInt(maxFeet) * 12 + parseInt(maxInches) > parseInt(minFeet) * 12 + parseInt(minInches))
      arr.push({ person_attribute_uuid: uuidMapping.height, contains: false, has_all_in_list: false, list: null, start_value: parseInt(minFeet) * 12 + parseInt(minInches), end_value: parseInt(maxFeet) * 12 + parseInt(maxInches) });

    if (weightValues.start_value && weightValues.end_value && parseInt(weightValues.end_value) > parseInt(weightValues.start_value))
      arr.push({ person_attribute_uuid: uuidMapping.weight, contains: false, has_all_in_list: false, list: null, start_value: parseInt(weightValues.start_value), end_value: parseInt(weightValues.end_value) });

    if (selectedEyeColor.length)
      arr.push({ person_attribute_uuid: uuidMapping.eyeColor, contains: false, start_value: null, end_value: null, has_all_in_list: false, list: selectedEyeColor });

    if (selectedHairColor.length)
      arr.push({ person_attribute_uuid: uuidMapping.hairColor, contains: false, start_value: null, end_value: null, has_all_in_list: false, list: selectedHairColor });

    const roleCompTemp = getRoleCompTemp();
    roleCompTemp["value"] = arr;
    triggerUpdateShiftComp({ roleCompDetails: roleCompTemp });
    updateRoleOnEdit({ person_search_obj: { replace_items: true, person_attributes: arr } });
  };

  return {
    isEditable, setIsEditable,
    roleCompTags, setRoleCompTags,
    ageValues, setAgeValues,
    weightValues, setWeightValues,
    minFeet, setMinFeet,
    minInches, setMinInches,
    maxFeet, setMaxFeet,
    maxInches, setMaxInches,
    selectedGender, setSelectedGender,
    selectedEthnicity, setSelectedEthnicity,
    selectedEyeColor, setSelectedEyeColor,
    selectedHairColor, setSelectedHairColor,
    showCompositionPopover, setShowCompositionPopover,
    showCompositionParent, setShowCompositionParent,
    updateRoleCompTemplate,
    addTagForEdit,
    onRoleCompositionChange,
    onCloseRoleCompTag,
    clearMemoryLeak,
    onSaveEdit,
  };
};
